import React, { useEffect, useState } from "react"
import { OrderHistoryProps } from "./OrderHistoryProps"
import { Order } from "Common/Models/Order"
import { axios_helper_async } from "helpers/Axios";
import withMessages, { WithMessagesProps } from "Common/withMessages";
import withNotifications, { WithNotificationsProps } from "Common/withNotifications";
import { MessageType } from "Common/Components/Messages/messageTypes";
import { NotificationType } from "Common/Components/Notifications/notificationTypes";
import { ChevronLeft } from "lucide-react";
import Title from "Common/Components/Titles/Title";
import { OrderProductDetailsView } from "Common/Models/OrderProductsDetailsView";
import withTranslation from "Common/withTranslation";
import { WithTranslationProps } from "Common/Components/Translation/WithTranslationProps";
import languages from "Common/constants/languages";
import { WithCurrencyProps } from "Common/Components/Currency/WithCurrencyProps";
import { withCurrency } from "Common/Components/Currency/withCurrency";

const OrderHistory: React.FC<OrderHistoryProps & WithMessagesProps & WithNotificationsProps & WithTranslationProps & WithCurrencyProps> = ({
  customerId,
  orderId: id,
  i18n,
  t,
  c,
  currency,
  onClose,
  notifications, messages
}) => {
  const [order, setOrder] = useState<Order | null>(null);
  const [orderProducts, setOrderProducts] = useState<OrderProductDetailsView[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const getOrder = async () => {
    try {
      setLoading(true);
      const body = {
        orderId: id
      }

      const response = await axios_helper_async<Order>("/Account/Get-Customer-Order-By-CustomerId", 'GET', body);

      messages.setMessages(response.messages);
      notifications.setNotifications(response.notifications);

      if (response.isSuccess) {
        if (response.data) {
          setOrder(response.data);
        }
      }
      else {
        messages.showMessage("UserProfile.Errors.GetCustomerOrder", MessageType.Error, "Error");
      }
    }
    catch (err: any) {
      notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
    }
    finally {
      setLoading(false);
    }
  }

  const getOrderPorducts = async () => {
    try {
      setLoading(true);
      const body = {
        orderId: id
      }

      const response = await axios_helper_async<OrderProductDetailsView[]>("/Account/Get-Customer-OrderProducts-By-OrderId", 'GET', body);

      messages.setMessages(response.messages);
      notifications.setNotifications(response.notifications);

      if (response.isSuccess) {
        if (response.data) {
          setOrderProducts(response.data);
        }
      }
      else {
        messages.showMessage("UserProfile.Errors.GetCustomerOrderProducts", MessageType.Error, "Error");
      }
    }
    catch (err: any) {
      notifications.showNotification("AxiosAPI.FailedFetch", NotificationType.Error);
    }
    finally {
      setLoading(false);
    }
  }


  useEffect(() => {
    if (customerId === 0 || id === 0) {
      setOrder(null);
    }
    else if (customerId !== 0 && id !== 0) {
      (async () => {
        await getOrder();
        await getOrderPorducts();
      })();
    }
  }, [customerId, id])

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex flex-row gap-5 w-full justify-start items-center">
        <div className="h-full bg-slate-300 rounded border cursor-pointer" onClick={onClose}>
          <ChevronLeft height={60}></ChevronLeft>
        </div>
        <div className="w-full items-center justify-center">
          <h1 className="font-bold text-4xl">{t('UserProfile.Orders.OrderHistory.Title')}: {id}</h1>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-3 justify-start items-start w-full">
        <div className="flex flex-col gap-2 justify-start items-start w-full">
          {loading ?
            <>
              <div className="flex flex-row gap-2 justify-between items-center w-full rounded-lg border border-gray-300 p-4">                
                <div className="flex flex-col gap-4 justify-stretch items-start p-4">
                  <h3 className="text-custom-500"><div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></h3>
                  <p><div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></p>
                  <div className="flex flex-row gap-2 divide-x justify-start items-center w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.Price')}: {currency.value} <div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                    <span className="pl-2">{t('UserProfile.Orders.OrderHistory.Quantity')}: <div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                </div>
                <div className="flex flex-col gap-1 justify-end items-center h-full">
                  {/* <img width={150} height={150} src={null}><div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></img> */}
                  <div className="py-2"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div>
                  <span>{t('UserProfile.Orders.OrderHistory.TotalPrice')}: {currency.value} <div className="py-2 w-56"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                </div>
              </div>
            </>
            :
            <>
              {orderProducts.map((product, index) => {
                return (
                  <div className="flex flex-row gap-2 justify-between items-center w-full rounded-lg border border-gray-300 p-4" key={index}>
                    <div className="flex flex-col gap-4 justify-stretch items-start p-4">
                      <a href={`/Product?category=${product.categoryId}&product=${product.productId}`} target="_blank" rel="noopener noreferrer"><h3 className="text-custom-500">{product.name}</h3></a>
                      <p>{product.description}</p>
                      <div className="flex flex-row gap-2 divide-x justify-start items-center w-full">
                        <span>{t('UserProfile.Orders.OrderHistory.Price')}: {currency.value} {c(product.price).toFixed(2)}</span>
                        <span className="pl-2">{t('UserProfile.Orders.OrderHistory.Quantity')}: {product.quantity}</span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1 justify-end items-center h-full">
                      <img width={150} height={150} src={product?.image}></img>
                      <span>{t('UserProfile.Orders.OrderHistory.TotalPrice')}: {currency.value} {c(product.quantity * product.price).toFixed(2)}</span>
                    </div>
                  </div>
                )
              })}
            </>
          }
        </div>
        <div className="flex flex-col rounded border p-4 border-gray-300 divide-y gap-3 w-full md:w-1/4">
          {
            loading ?
              <>
                <div className="flex flex-col justify-between items-center gap-2">
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderDate')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderShipmentMethod')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderPaymentMethod')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                </div>
                <div className="flex flex-col justify-between items-center gap-2 pt-2">
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderSubtotal')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderShippingAmount')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderTaxAmount')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderTotalAmount')}</span>
                    <span><div className="py-2 w-20"><div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div></div></span>
                  </div>
                </div>
              </>
              :
              <>
                <div className="flex flex-col justify-between items-center gap-2">
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderDate')}</span>
                    {/* <span>{order?.orderDate.toLocaleDateString()}</span>*/}
                    <span>{order?.orderDate && new Date(order.orderDate).toLocaleDateString(`${i18n.language === languages.es.label ? `${i18n.language}-MX` : `${i18n.language}-US`}`)}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderShipmentMethod')}</span>
                    <span>{order?.shippingMethod}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderPaymentMethod')}</span>
                    <span>{order?.paymentMethod}</span>
                  </div>
                </div>
                <div className="flex flex-col justify-between items-center gap-2 pt-2">
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderSubtotal')}</span>
                    <span>{currency.value} {c(order?.subtotalAmount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderShippingAmount')}</span>
                    <span>{currency.value} {c(order?.shippingCost ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderTaxAmount')}</span>
                    <span>{currency.value} {c(order?.taxAmount ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderExtraFee')}</span>
                    <span>{currency.value} {c(order?.extraFee ?? 0).toFixed(2)}</span>
                  </div>
                  <div className="flex flex-row justify-between items-center gap-6 w-full">
                    <span>{t('UserProfile.Orders.OrderHistory.OrderTotalAmount')}</span>
                    <span>{currency.value} {c(order?.totalAmount ?? 0).toFixed(2)}</span>
                  </div>
                </div>
              </>
          }
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-5 md:gap-2 w-full justify-start items-start">
        <div className="flex flex-col gap-2 w-fit justify-center items-start bg-slate-50 border border-gray-300 rounded-xl p-4">
          {!loading ? (
            <>
              <h3 className="text-custom-500">{t("UserProfile.BillingAddressEditor.Title")}</h3>
              <table className="text-left">
                <tbody>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Street")}:</td>
                    <td className="py-0">{order?.billingStreet ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.AddressLine")}:</td>
                    <td className="py-0">{order?.billingAddressLine ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.City")}:</td>
                    <td className="py-0">{order?.billingCity ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.State")}:</td>
                    <td className="py-0">{order?.billingState ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.ZipCode")}:</td>
                    <td className="py-0">{order?.billingZipCode ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Country")}:</td>
                    <td className="py-0">{order?.billingCountry ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Company")}:</td>
                    <td className="py-0">{order?.billingCompany ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.BillingAddressEditor.Phone")}:</td>
                    <td className="py-0">{order?.billingPhone ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
            // Animación de carga si está cargando
            <>
              <h3 className="text-custom-500">{t("UserProfile.BillingAddressEditor.Title")}</h3>
              <table className="text-left">
                <tbody>
                  {['Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                    <tr key={index}>
                      <td className="font-bold pr-4">{t(`UserProfile.BillingAddressEditor.${label}`)}:</td>
                      <td className="py-1 w-56">
                        <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
        <div className="flex flex-col gap-2 w-fit justify-center items-start bg-slate-50 border border-gray-300 rounded-xl p-4">
          {!loading ? (
            <>
              <h3 className="text-custom-500">{t("UserProfile.ShippingAddressEditor.Title")}</h3>
              <table className="text-left">
                <tbody>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Street")}:</td>
                    <td className="py-0">{order?.shippingStreet ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.AddressLine")}:</td>
                    <td className="py-0">{order?.shippingAddressLine ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.City")}:</td>
                    <td className="py-0">{order?.shippingCity ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.State")}:</td>
                    <td className="py-0">{order?.shippingState ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.ZipCode")}:</td>
                    <td className="py-0">{order?.shippingZipCode ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Country")}:</td>
                    <td className="py-0">{order?.shippingCountry ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Company")}:</td>
                    <td className="py-0">{order?.shippingCompany ?? "-"}</td>
                  </tr>
                  <tr>
                    <td className="font-bold pr-4">{t("UserProfile.ShippingAddressEditor.Phone")}:</td>
                    <td className="py-0">{order?.shippingPhone ?? "-"}</td>
                  </tr>
                </tbody>
              </table>
            </>
          ) : (
            // Animación de carga si está cargando
            <>
              <h3 className="text-custom-500">{t("UserProfile.ShippingAddressEditor.Title")}</h3>
              <table className="text-left">
                <tbody>
                  {['Street', 'AddressLine', 'City', 'State', 'ZipCode', 'Country', 'Company', 'Phone'].map((label, index) => (
                    <tr key={index}>
                      <td className="font-bold pr-4">{t(`UserProfile.ShippingAddressEditor.${label}`)}:</td>
                      <td className="py-1 w-56">
                        <div className="w-full h-6 bg-gray-300 animate-pulse rounded"></div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default withMessages(withNotifications(withTranslation(withCurrency(OrderHistory))));